import { useEffect, useMemo, useState } from 'react';
import { Building2, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Shared/Button';
import MapPicker from '@/components/Shared/MapPicker';
import { Modal } from '@/components/Shared/Modal';
import SearchSelect, { type SearchSelectOption } from '@/components/Shared/SearchSelect';
import { useObjectStore } from '@/store/useObjectStore';
import { usePlanningAssistantStore } from '@/store/usePlanningAssistantStore';
import { formatDate, parseIsoDate } from '@/utils/dateUtils';
import type { GeocodeAddress } from '@/utils/geocoding';
import { firstName } from '../assistantFormat';
import type { AssistantEmployee, AssistantObject } from '../assistantTypes';

type Tab = 'object' | 'map';
type Point = { latitude: number; longitude: number };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employee: AssistantEmployee;
  date: string;
  /** Objects of the day (assistant payload) — offered first and the source of short names. */
  objects: Map<string, AssistantObject>;
}

interface ObjectOption extends SearchSelectOption, Point {}

/** "Straße Nr, Ort" from a geocoder hit; null when nothing usable came back. */
function addressLabel(a: GeocodeAddress): string | null {
  const street = [a.street, a.houseNumber].filter(Boolean).join(' ');
  return [street, a.city].filter(Boolean).join(', ') || null;
}

function hasCoords(lat: number | null | undefined, lng: number | null | undefined): boolean {
  return lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
}

/** Overrides where the employee starts on `date`: an object with coordinates or a point on the map. */
export function StartPointDialog({ isOpen, onClose, employee, date, objects }: Props) {
  const { t } = useTranslation('planning');
  const setStartPoint = usePlanningAssistantStore((s) => s.setStartPoint);
  const busy = usePlanningAssistantStore((s) => s.busy);
  const allObjects = useObjectStore((s) => s.objects);
  const objectsLoading = useObjectStore((s) => s.loading);

  const current = employee.startPoint;
  const isOverride = current?.source === 'Override';
  const currentPoint = useMemo<Point | null>(
    () =>
      hasCoords(current?.latitude, current?.longitude)
        ? { latitude: current?.latitude as number, longitude: current?.longitude as number }
        : null,
    [current?.latitude, current?.longitude],
  );

  const [tab, setTab] = useState<Tab>('object');
  const [objectId, setObjectId] = useState('');
  const [point, setPoint] = useState<Point | null>(null);
  const [pointLabel, setPointLabel] = useState<string | null>(null);
  const [pointTouched, setPointTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTab(isOverride ? 'map' : 'object');
    setObjectId('');
    setPoint(currentPoint);
    setPointLabel(isOverride ? current?.label ?? null : null);
    setPointTouched(false);
    void useObjectStore.getState().fetchAllObjects({ isActive: true });
    // why: reset only when the dialog opens — a silent refetch must not discard the selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const dayOptions = useMemo<ObjectOption[]>(
    () =>
      [...objects.values()]
        .filter((o) => o.objectId && hasCoords(o.latitude, o.longitude))
        .map((o) => ({
          id: o.objectId as string,
          label: o.shortName || o.name || '',
          description: o.shortName && o.name && o.shortName !== o.name ? o.name : undefined,
          latitude: o.latitude as number,
          longitude: o.longitude as number,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [objects],
  );

  const allOptions = useMemo<ObjectOption[]>(
    () =>
      allObjects
        .filter((o) => o.id && hasCoords(o.latitude, o.longitude) && (o.status ?? '').toLowerCase() !== 'inactive')
        .map((o) => {
          const a = o.address;
          const street = [a?.street, a?.streetNumber].filter(Boolean).join(' ');
          const address = [street, [a?.postalCode, a?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
          // The objects list has no short name; the day's payload has it for its objects.
          const known = objects.get(o.id as string);
          return {
            id: o.id as string,
            label: known?.shortName || known?.name || o.name || '',
            description: address || undefined,
            latitude: o.latitude as number,
            longitude: o.longitude as number,
          };
        }),
    [allObjects, objects],
  );

  const selectedObject =
    allOptions.find((o) => o.id === objectId) ?? dayOptions.find((o) => o.id === objectId) ?? null;

  const canSave = !busy && (tab === 'object' ? !!selectedObject : pointTouched && !!point);

  const save = async () => {
    if (!canSave) return;
    const value =
      tab === 'object' && selectedObject
        ? { latitude: selectedObject.latitude, longitude: selectedObject.longitude, label: selectedObject.label || null }
        : point
          ? { ...point, label: pointLabel || null }
          : null;
    if (!value) return;
    const ok = await setStartPoint(employee.employeeId ?? '', value);
    if (ok) onClose();
  };

  const tabs: { key: Tab; icon: typeof MapPin }[] = [
    { key: 'object', icon: Building2 },
    { key: 'map', icon: MapPin },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={t('assistant.startPoint.dialogTitle', {
        name: firstName(employee.name),
        date: formatDate(parseIsoDate(date)),
      })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('assistant.startPoint.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={!canSave} loading={busy}>
            {t('assistant.startPoint.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[12px] text-slate-500">{t('assistant.startPoint.hint')}</p>
        {!employee.planningEntryId && (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
            {t('assistant.startPoint.createsWorkDay')}
          </p>
        )}

        <div className="flex self-start p-0.5 rounded-lg bg-slate-100" role="tablist">
          {tabs.map(({ key, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[12px] ${
                tab === key ? 'bg-white text-slate-900 font-medium shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Icon size={13} strokeWidth={2} />
              {t(`assistant.startPoint.tabs.${key}`)}
            </button>
          ))}
        </div>

        {tab === 'object' ? (
          <div className="flex flex-col gap-1.5 min-h-[280px]">
            <SearchSelect
              options={allOptions}
              suggestedOptions={dayOptions}
              suggestedLabel={t('assistant.startPoint.objectsToday')}
              optionsLabel={t('assistant.startPoint.objectsAll')}
              value={objectId}
              onChange={setObjectId}
              placeholder={t('assistant.startPoint.objectSearch')}
              noResultsText={t('assistant.startPoint.noObjects')}
              label={t('assistant.startPoint.objectLabel')}
              inlineDropdown
            />
            <span className="text-[11px] text-slate-500">
              {objectsLoading ? t('assistant.startPoint.objectsLoading') : t('assistant.startPoint.objectsHint')}
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <MapPicker
              value={point}
              onChange={(value) => {
                setPoint(value);
                // A click or drag has no address; a search hit sets it right after (onAddressResolved).
                setPointLabel(null);
                setPointTouched(true);
              }}
              onAddressResolved={(address) => setPointLabel(addressLabel(address))}
              showSearch
              heightClassName="h-72"
            />
            {point && pointTouched && (
              <span className="text-[12px] text-slate-600">
                {t('assistant.startPoint.chosen', {
                  label: pointLabel || t('assistant.startPoint.customPoint'),
                })}
              </span>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
